import type { ChatSession } from "@/types";

/**
 * Merging a single conversation, read by id, into the paged inbox list.
 *
 * Pure and separate from the store because the interesting case is a race,
 * and a race is only testable if the decision is a function. `ensureChat`
 * checks the list, awaits a network read, and merges — and between those two
 * points the list can gain the very row being fetched, from a refresh or a
 * socket patch. Appending unconditionally puts it in twice, which reaches the
 * FlatList as a duplicate key.
 *
 * The existing row WINS. It came from the paged list, which is the source of
 * ordering and unread counts; a by-id read knows neither and would overwrite
 * a fresher `unreadCount` with a stale one.
 */
export function mergeChatSession(
  chats: ChatSession[],
  incoming: ChatSession,
): ChatSession[] {
  return chats.some((chat) => chat.id === incoming.id)
    ? chats
    : [...chats, incoming];
}

/** Whether the list is missing this conversation and a read is warranted. */
export function needsChatSession(
  chats: ChatSession[],
  chatId: string | undefined,
): boolean {
  if (!chatId) return false;
  return !chats.some((chat) => chat.id === chatId);
}
